import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="catalog--attribute-schema"
// Dynamically refreshes attribute fields via Turbo Frame when kind or categories change.
//
// CONEK-4351: Captures unsaved attribute field values before the schema refresh and
// restores them on the new HTML (off-document) before DOM insertion. This preserves
// user input when the attribute schema changes due to category/kind selection.
//
// The restore logic matches fields by `name` attribute. If _attribute_fields.html.erb
// changes field name patterns, this capture/restore logic must be updated in sync.
export default class extends Controller {
  static values = {
    url: String,
    itemId: String,
  }

  connect() {
    this._bindFormChanges()
  }

  disconnect() {
    this._unbindFormChanges()
    if (this._abortController) this._abortController.abort()
  }

  // ── Private ──────────────────────────────────────────────────────────

  _bindFormChanges() {
    const form = this.element.closest("form")
    if (!form) return

    this._form = form
    this._handleFormChangeBound = this._handleFormChange.bind(this)

    // Single form-level delegation handles kind, category, and MultiSelectComponent changes
    form.addEventListener("change", this._handleFormChangeBound)
  }

  _unbindFormChanges() {
    if (this._form && this._handleFormChangeBound) {
      this._form.removeEventListener("change", this._handleFormChangeBound)
    }
  }

  _handleFormChange(event) {
    const target = event.target
    if (!target) return

    const name = target.getAttribute("name")
    if (name === "item[kind]" || (name && name.includes("category_ids"))) {
      this._refreshAttributes()
    }
  }

  async _refreshAttributes() {
    // Cancel any in-flight request (CONEK-4351: prevents race conditions on rapid changes)
    if (this._abortController) this._abortController.abort()
    this._abortController = new AbortController()

    const form = this.element.closest("form")
    if (!form) return

    // Capture current attribute values BEFORE the fetch
    const capturedValues = this._captureAttributeValues()

    const kind = form.querySelector('[name="item[kind]"]')?.value
    const categorySelects = form.querySelectorAll(
      '[name="item[category_ids][]"]',
    )
    const categoryIds = Array.from(categorySelects)
      .map((el) => el.value)
      .filter(Boolean)

    // Build query params
    const params = new URLSearchParams()
    if (kind) params.append("kind", kind)
    categoryIds.forEach((id) => params.append("category_ids[]", id))

    const url = `${this.urlValue}?${params.toString()}`

    try {
      const response = await get(url, {
        responseKind: "turbo-stream",
        headers: { Accept: "text/html" },
        signal: this._abortController.signal,
      })

      if (response.ok) {
        const html = await response.text
        const frame = this.element.querySelector("#attribute-schema-fields")
        if (frame) {
          // Parse server HTML safely via DOMParser (defense-in-depth against XSS)
          const doc = new DOMParser().parseFromString(html, "text/html")
          const newFrame = doc.querySelector("#attribute-schema-fields")
          if (newFrame) {
            // Restore captured values on the off-document node BEFORE insertion.
            // This ensures Tom Select reads correct <option selected> when the
            // ui--select Stimulus controller connect() fires after replaceWith.
            this._restoreAttributeValues(newFrame, capturedValues)
            frame.replaceWith(newFrame)
          }
        }
      }
    } catch (error) {
      if (error.name === "AbortError") return // superseded by a newer request
      console.error("[attribute-schema] Failed to refresh attributes:", error)
    }
  }

  // ── Value Capture (CONEK-4351) ─────────────────────────────────────

  /**
   * Reads all current attribute field values from the DOM.
   * Returns a Map keyed by field name -> { type, value/checked/values }.
   *
   * Handles: string, integer, float, measurement (text/number inputs),
   * boolean (checkbox), enum single/colour (select), enum array (multi-select).
   * Skips: file inputs (browser security prevents reading their value).
   */
  _captureAttributeValues() {
    const frame = this.element.querySelector("#attribute-schema-fields")
    if (!frame) return new Map()

    const captured = new Map()
    const ATTR_PATTERN = /^item\[attribute_values\]/

    // Text/number inputs (string, integer, float, measurement)
    frame
      .querySelectorAll('input[type="text"], input[type="number"]')
      .forEach((input) => {
        if (ATTR_PATTERN.test(input.name)) {
          captured.set(input.name, { type: "text", value: input.value })
        }
      })

    // Checkboxes (boolean) — skip the hidden pair that shares the same name
    frame.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (ATTR_PATTERN.test(input.name)) {
        captured.set(input.name, { type: "checkbox", checked: input.checked })
      }
    })

    // Single selects (enum single, colour)
    frame.querySelectorAll("select:not([multiple])").forEach((select) => {
      if (ATTR_PATTERN.test(select.name)) {
        captured.set(select.name, { type: "select", value: select.value })
      }
    })

    // Multi-selects (enum array) — read from selectedOptions, not hidden sentinels
    frame.querySelectorAll("select[multiple]").forEach((select) => {
      if (ATTR_PATTERN.test(select.name)) {
        const values = Array.from(select.selectedOptions).map((o) => o.value)
        captured.set(select.name, { type: "multi-select", values })
      }
    })

    return captured
  }

  // ── Value Restore (CONEK-4351) ─────────────────────────────────────

  /**
   * Patches the parsed (off-document) HTML with previously captured values.
   * Must run BEFORE replaceWith so Tom Select initializes with correct state.
   *
   * Only restores values for fields present in both old and new schema.
   * Fields removed by category change are skipped (querySelector returns null).
   * Fields added by category change keep their server-rendered defaults.
   */
  _restoreAttributeValues(newFrame, capturedValues) {
    if (!capturedValues || capturedValues.size === 0) return

    for (const [name, entry] of capturedValues) {
      switch (entry.type) {
        case "text": {
          const input = newFrame.querySelector(
            `input[name="${CSS.escape(name)}"]`,
          )
          if (input && entry.value) {
            input.setAttribute("value", entry.value)
          }
          break
        }

        case "checkbox": {
          // Find the checkbox (not the hidden input that shares the same name)
          const checkbox = newFrame.querySelector(
            `input[type="checkbox"][name="${CSS.escape(name)}"]`,
          )
          if (checkbox) {
            if (entry.checked) {
              checkbox.setAttribute("checked", "")
            } else {
              checkbox.removeAttribute("checked")
            }
          }
          break
        }

        case "select": {
          const select = newFrame.querySelector(
            `select[name="${CSS.escape(name)}"]`,
          )
          if (select && entry.value) {
            select.querySelectorAll("option").forEach((option) => {
              if (option.value === entry.value) {
                option.setAttribute("selected", "")
              } else {
                option.removeAttribute("selected")
              }
            })
          }
          break
        }

        case "multi-select": {
          const select = newFrame.querySelector(
            `select[name="${CSS.escape(name)}"]`,
          )
          if (select && entry.values.length > 0) {
            const valueSet = new Set(entry.values)
            select.querySelectorAll("option").forEach((option) => {
              if (valueSet.has(option.value)) {
                option.setAttribute("selected", "")
              } else {
                option.removeAttribute("selected")
              }
            })
          }
          break
        }
      }
    }
  }
}
